/**
 * Copyright (C) 2026 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { Box, Typography } from '@mui/material';
import React from 'react';
import { WhatsNewBullet, WhatsNewFeature, WhatsNewRelease } from '../../../shared/entities/whatsNew';
import appTheme from '../theme/appTheme.json';

const FEATURE_ROW_SPACING = 10;

interface WhatsNewBulletsProps {
  bullets: WhatsNewBullet[];
  level: number;
}

// Renders nested markdown bullets recursively.
const WhatsNewBullets: React.FC<WhatsNewBulletsProps> = ({ bullets, level }) => {
  if (!bullets.length) {
    return null;
  }

  return (
    <Box
      component="ul"
      sx={{
        margin: 0,
        marginTop: level === 0 ? '8px' : '4px',
        marginBottom: 0,
        paddingLeft: level === 0 ? '22px' : '20px',
      }}
    >
      {bullets.map((bullet: WhatsNewBullet, index: number) => (
        <Box component="li" key={`${bullet.text}-${level}-${index}`} sx={{ marginBottom: '4px' }}>
          <Typography
            sx={{
              color: appTheme.primaryTextColor,
              fontFamily: appTheme.appFontFamily,
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: '24px',
            }}
          >
            {bullet.text}
          </Typography>
          <WhatsNewBullets bullets={bullet.children} level={level + 1} />
        </Box>
      ))}
    </Box>
  );
};

const WhatsNewFeatureSection: React.FC<{ feature: WhatsNewFeature }> = ({ feature }) => {
  return (
    <Box
      sx={{
        padding: '12px 16px 16px',
        backgroundColor: appTheme.mptPrimaryBackgroundColor,
        borderRadius: '6px',
        width: '100%',
      }}
    >
      <Typography
        sx={{
          color: appTheme.primaryTextColor,
          fontFamily: appTheme.appFontFamily,
          fontSize: '18px',
          fontWeight: 700,
          lineHeight: '24px',
        }}
      >
        {feature.title}
      </Typography>
      <WhatsNewBullets bullets={feature.bullets} level={0} />
    </Box>
  );
};

export interface WhatsNewReleaseSectionProps {
  release: WhatsNewRelease;
}

// Renders a single release block with timeline markers for each feature card.
const WhatsNewReleaseSection: React.FC<WhatsNewReleaseSectionProps> = ({ release }) => {
  return (
    <Box sx={{ marginBottom: '28px' }}>
      <Typography
        sx={{
          color: appTheme.primaryTextColor,
          fontFamily: appTheme.viasatFontFamilyHeavy,
          fontSize: '16px',
          fontWeight: 800,
          lineHeight: '28px',
          letterSpacing: '0.2px',
          textTransform: 'uppercase',
          marginBottom: '8px',
        }}
      >
        {release.releaseDate}
      </Typography>
      {release.features.map((feature: WhatsNewFeature, index: number) => {
        const isLastFeature = index === release.features.length - 1;

        return (
          <Box
            key={`${release.releaseDate}-${feature.title}-${index}`}
            sx={{
              display: 'flex',
              alignItems: 'stretch',
              columnGap: '12px',
              marginBottom: isLastFeature ? 0 : `${FEATURE_ROW_SPACING}px`,
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '12px',
                flexShrink: 0,
                marginLeft: '14px',
                // Timeline dot aligned with the feature card content area.
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: '50%',
                  top: '20px',
                  transform: 'translateX(-50%)',
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  backgroundColor: appTheme.mptGray,
                },
                ...(!isLastFeature
                  ? {
                      // Connector line to the next feature card in the same release.
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        left: '50%',
                        top: '24px',
                        transform: 'translateX(-50%)',
                        width: '2px',
                        height: `calc(100% + ${FEATURE_ROW_SPACING}px)`,
                        backgroundColor: appTheme.mptGray,
                      },
                    }
                  : {}),
              }}
            />

            <WhatsNewFeatureSection feature={feature} />
          </Box>
        );
      })}
    </Box>
  );
};

export default WhatsNewReleaseSection;
